import { FieldErrorsImpl } from 'react-hook-form'
import { CustomerModel } from 'pages/Customers/helpers/types'
import { RiskScore } from 'pages/EmbeddedCustomers/helpers/types'
import { WithPaginationApiResponseExtended } from './pagination'
import { PaginationQueryParams } from './reactQuery'
import { Currencies, Pageable, Sort, WalletTypes } from './types'

export const businessCustomerFields: AddCustomerPayloadKeys[] = [
  'customerEntityType',
  'businessName',
  'industry',
  'businessDescription',
  'registrationNumber',
  'businessMonthlyVolume',
  'businessTaxResidency',
  'countryCode',
  'addressLine1',
  'city',
  'postalCode',
  // Business representitive details
  'firstName',
  'lastName',
  'dateOfBirth',
  'nationality',
]

export const individualCustomerFields: AddCustomerPayloadKeys[] = [
  'customerEntityType',
  'firstName',
  'lastName',
  'dateOfBirth',
  'personalIdNumber',
  'countryCode',
  'addressLine1',
  'city',
  'postalCode',
  'nationality',
  'stateCode',
]

export const CustomerEntityType = {
  COMPANY: 'COMPANY',
  INDIVIDUAL: 'INDIVIDUAL',
} as const

export type CustomerEntityType = (typeof CustomerEntityType)[keyof typeof CustomerEntityType]

export const CustomerStatus = {
  PENDING: 'PENDING',
  VERIFIED: 'VERIFIED',
  REJECTED: 'REJECTED',
} as const

export type CustomerStatusType = (typeof CustomerStatus)[keyof typeof CustomerStatus]

export type CustomerStatusHighlightType = 'success' | 'warning' | 'error' | 'neutral'

export const CustomerDocumentStatus = {
  PENDING: 'PENDING',
  APPROVED: 'APPROVED',
  INT: 'INT',
  INFO_REQUIRED: 'INFO_REQUIRED',
  DECLINE: 'DECLINED',
  REJECTED: 'REJECTED',
  VERIFIED: 'VERIFIED',
}

export type CustomerDocumentStatusType =
  (typeof CustomerDocumentStatus)[keyof typeof CustomerDocumentStatus]

export const AccountType = {
  VIRTUAL_ACCOUNT: 'VIRTUAL_ACCOUNT',
  DEPOSIT_ACCOUNT: 'DEPOSIT_ACCOUNT',
} as const

export type AccountType = (typeof AccountType)[keyof typeof AccountType]

export interface CustomerShared<StatusType> {
  reference: string
  externalReference?: string
  status: StatusType
  type: CustomerEntityType
}

export type CustomerSummary = {
  name: string
  description: string
}

export type CustomerType = CustomerShared<CustomerStatusType>

export type Customer = CustomerType & CustomerSummary

export interface CustomerAddress {
  addressLine1: string
  addressLine2?: string
  city: string
  postalCode: string
  countryCode: string
  stateCode?: string
}
interface AddBusinessCustomerRequestDetails {
  name: string
  description: string
  taxResidenceCountryCode: string
  registrationNumber: string
  industryReference: string
  monthlyExpectedVolumesReference: string
  address: CustomerAddress
  representative: {
    firstName: string
    lastName: string
    dateOfBirth: string
    address: { countryCode: string }
    birthCountryCode?: string
  }
}

interface AddIndividualCustomerRequestDetails {
  firstName: string
  lastName: string
  dateOfBirth: string
  documentNumber: string
  // @deprecated
  birthCountryCode: string
  nationality?: string
  residenceCountryCode: string
  address: CustomerAddress
  description: string
}
export interface AddCustomerRequest {
  type: string
  externalReference: string | null
  company?: AddBusinessCustomerRequestDetails
  individual?: AddIndividualCustomerRequestDetails
}

export interface Address {
  addressLine1: string
  addressLine2?: string | null
  city: string
  postalCode: string
  countryCode: string
  stateCode?: string
  country: string
}

export interface MonthlyExpectedVolumes {
  reference: string
  name: string
  min: number
  max: number
}

export interface Industry {
  reference: string
  name: string
}

export interface CustomerPerson {
  firstName: string
  lastName: string
  dateOfBirth: string
  address: Address
}

export interface Representative extends CustomerPerson {
  countryCode: string
}

export interface Business {
  name: string
  description: string
  taxResidenceCountryCode: string
  registrationNumber: string
  address: Address
  representative: Representative
  industry: Industry
  monthlyExpectedVolumes: MonthlyExpectedVolumes
}

export interface IndividualDetails {
  birthCountryCode: string
  documentNumber: string
}

export interface Individual {
  description?: string
  details: IndividualDetails
  person: {
    firstName: string
    lastName: string
    dateOfBirth: string
    address: Address
  }
}

export interface BusinessCustomerType extends CustomerType {
  company: Business
  reference: string
  riskScore?: RiskScore
}

export interface IndividualCustomerType extends CustomerType {
  individual: Individual
}

export type NestedCustomerDetails = BusinessCustomerType | IndividualCustomerType

export interface AddCustomerPayload {
  // General
  customerEntityType?: CustomerEntityType
  customerType?: string
  externalReference?: string
  countryCode?: string
  countryName?: string
  stateCode?: string
  stateName?: string
  addressLine1?: string
  addressLine2?: string | null
  city?: string
  postalCode?: string
  nationality?: string
  nationalityName?: string
  // Individual
  firstName?: string
  lastName?: string
  dateOfBirth?: string
  email?: string
  phoneNumber?: string
  annualSalary?: string
  personalIdNumber?: string
  individualCustomerDescription?: string | null
  jobTitle: string
  sourceOfFunds: string
  // Business
  businessName?: string
  industry?: string
  industryName?: string
  businessDescription?: string
  registrationNumber?: string
  businessMonthlyVolume?: string
  businessMonthlyVolumeName?: string
  businessTaxResidency?: string
  businessTaxResidencyName?: string
  riskScore?: RiskScore
  eddCompleted?: boolean
}

export type AddCustomerPayloadKeys = keyof AddCustomerPayload

export type AddCustomerFormErrors = Partial<FieldErrorsImpl<AddCustomerPayload>>

export const LedgerWalletType = {
  FIAT: 'FIAT',
  CRYPTO: 'CRYPTO',
}

export type LedgerType = (typeof LedgerWalletType)[keyof typeof LedgerWalletType]

export interface MonthlyVolume {
  id: number
  max: number
  min: number
  name: string
  reference: string
}

export interface Industries {
  reference: string
  name: string
  children: Array<{
    reference: string
    name: string
  }>
}

export interface Instructions {
  type: WalletTypes // FIAT or CRYPTO
  virtualAccountRequired: boolean
  walletProfile: string
}

export interface AddCustomerWalletRequest {
  currencyCode: Currencies
  name: string
  customerReference: string
  instruction: Instructions
}

export interface WalletBalance {
  value: number
  currencyCode: string
}

export interface WalletResponse {
  id: string
  accountReference: string
  customerReference: string
  name: string
  status: string
  balance: WalletBalance
  ledgers: any[]
}
export interface CustomerWalletsResponse {
  content: CustomerWallet[]
  pageable: Pageable
  totalPages: number
  totalElements: number
  last: boolean
  first: boolean
  sort: Sort
  numberOfElements: number
  size: number
  number: number
  empty: boolean
}

export interface CustomerWalletV2Balance {
  amount: number
  currency: string
}

export interface CustomerWalletV2Capabilities {
  safeguarded: boolean
}

export interface CustomerWalletV2
  extends Omit<
    CustomerWallet,
    'accountReference' | 'customerReference' | 'balance' | 'capabilities'
  > {
  customerId: string
  balance: CustomerWalletV2Balance
  capabilities: CustomerWalletV2Capabilities
}

export interface CustomerWalletsV2Response {
  content: CustomerWalletV2[]
  pageable: { pageNumber: number; pageSize: number }
  hasNext: boolean
}

export const CustomerWalletStatusType = {
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
  TERMINATED: 'TERMINATED',
} as const

export type CustomerWalletStatus =
  (typeof CustomerWalletStatusType)[keyof typeof CustomerWalletStatusType]

export const LedgerWalletCapabilities = {
  SAFEGUARDED: 'SAFEGUARDED',
} as const

export type LedgerWalletCapability =
  (typeof LedgerWalletCapabilities)[keyof typeof LedgerWalletCapabilities]

export interface CustomerWallet {
  id: string
  accountReference: string
  customerReference: string
  name: string
  status: CustomerWalletStatus
  balance: Balance
  ledgers: Ledger[]
  capabilities: LedgerWalletCapability[]
}

export interface OrchestratedCustomerWallet extends CustomerWallet {
  supportsThirdParty: boolean
  supportsDeposits: boolean
  supportsWithdrawals: boolean
  supportsInternalTransfers: boolean
}

export interface CustomerWalletDetails extends CustomerWallet {
  legacyWalletId: number
}

export interface CustomerWalletDepositDetails {
  wallet: CustomerWallet
  customer: NestedCustomerDetails
}

export const AccountNumberTypes = {
  IBAN: 'IBAN',
  SCAN: 'SCAN',
  SWIFT: 'SWIFT',
} as const

export type AccountNumberType = (typeof AccountNumberTypes)[keyof typeof AccountNumberTypes]

export interface Ledger {
  status?: string
  type: LedgerType
  // FIAT
  accountNumberFormat?: AccountNumberType
  accountNumber?: string
  code?: string
  accountHolderName?: string
  paymentReference?: string
  // CRYPTO
  address?: string
  network?: string
  tag?: string
}

export interface Balance {
  value: number
  currencyCode: string
}

export type CustomersFilters = {
  models?: CustomerModel[]
  types?: CustomerEntityType[]
  statuses?: string[]
}

export interface CustomersRequest extends PaginationQueryParams {
  search?: string
  filters?: CustomersFilters
}

export interface CustomerWalletsRequest extends PaginationQueryParams {
  customerReference: string
  currencyCodeSearch?: string
  currencyCodes?: string[]
  currencyType?: LedgerType
  searchTerm?: string
  statuses?: CustomerWalletStatus[]
}

export interface GetCustomerWalletProfilesRequest {
  currencyCodes?: string[]
  paymentMethods?: string[]
}

export interface WalletProfile extends GetCustomerWalletProfilesRequest {
  reference: string
}
export interface CustomerWalletProfile {
  profiles: WalletProfile[]
}

export interface AssignCustomerFeeWalletPayload {
  destinationWalletId: string
  currency: string
}

export interface AssignCustomerFeeWalletResponse {
  // add response
}

export interface GetCustomerFeeWalletsPayload {
  currency: string
}

export interface FeeWallet {
  destinationWalletId: string
  currency: string
}

export interface GetCustomerFeeWalletsResponse
  extends WithPaginationApiResponseExtended<FeeWallet[]> {}

export interface SetCustomerFeeFormData {
  customerId: string
  payinsEnabled: boolean
  payoutsEnabled: boolean
  currency: string
  feeStructure: string
  fixedFee: string
  percentageFee: string
}

export type SetCustomerFeeFormKeys = keyof SetCustomerFeeFormData
export interface ScrollToFieldOptions {
  position?: number
  element?: HTMLElement
}

export type ScrollToField = (options?: ScrollToFieldOptions) => void
